import type { NodeType } from '@tiptap/pm/model';
import { NodeSelection, Plugin, PluginKey, TextSelection, type Transaction } from '@tiptap/pm/state';

/**
 * Where typing goes right after the node that ends at `after`: the empty
 * textblock right there, or an empty paragraph added there — never the start of
 * the text that follows, which would glue the new words onto it. Adds that
 * paragraph to `tr` and returns the caret position in `tr.doc`, or null when
 * nothing typed may follow the node there.
 */
export const caretAfter = (tr: Transaction, after: number): number | null => {
  const $after = tr.doc.resolve(after);

  if ($after.parent.inlineContent) {
    return after;
  }

  const next = $after.nodeAfter;

  if (next?.isTextblock && next.content.size === 0) {
    return after + 1;
  }

  const paragraph = tr.doc.type.schema.nodes.paragraph;
  const index = $after.index();

  if (!paragraph || !$after.parent.canReplaceWith(index, index, paragraph)) {
    return null;
  }

  tr.insert(after, paragraph.create());

  return after + 1;
};

/** The selected node, when it is one of `type`. */
const selectedNodeOf = (selection: unknown, type: NodeType): NodeSelection | null =>
  (selection instanceof NodeSelection && selection.node.type === type ? selection : null);

/**
 * Typing or pasting never replaces an attachment. ProseMirror writes over a
 * selected node, and an image or a file card gets selected by the same click
 * that puts the cursor into a composer — the card fills the composer's middle —
 * so the first letter of the message, or the answer pasted in, would delete the
 * file without a word. With one selected, the text goes right after it instead;
 * removing an attachment stays a deliberate act (Backspace, Delete, the card's ×).
 */
export const typeAfterSelectedNode = (type: NodeType): Plugin => new Plugin({
  key: new PluginKey(`${type.name}TypeAfter`),
  props: {
    handleTextInput: (view, _from, _to, text) => {
      const selection = selectedNodeOf(view.state.selection, type);
      const tr = view.state.tr;
      const caret = selection ? caretAfter(tr, selection.to) : null;

      if (caret === null) {
        return false;
      }

      tr.insertText(text, caret);
      tr.setSelection(TextSelection.create(tr.doc, caret + text.length));
      view.dispatch(tr.scrollIntoView());

      return true;
    },

    handleDOMEvents: {
      // Only moves the cursor, and before any `handlePaste` runs: tiptap orders
      // plugins of equal priority in reverse, so Markdown and file pastes come
      // ahead of this one. The paste itself stays with whichever handler takes
      // it (Markdown, files, ProseMirror's own) and lands after the node.
      paste: (view) => {
        const selection = selectedNodeOf(view.state.selection, type);
        const tr = view.state.tr;
        const caret = selection ? caretAfter(tr, selection.to) : null;

        if (caret !== null) {
          view.dispatch(tr.setSelection(TextSelection.create(tr.doc, caret)));
        }

        return false;
      },
    },
  },
});
